/**
 * The channel header's bell (2026-10-29, Discord's "Notification
 * Settings" button): its menu holds Mute channel and the levels; it shows
 * the bell struck through while the channel is muted. Server channels only,
 * and only while the Console's `notifications` flag is on.
 *
 * A phone's header keeps at most three actions (§12.7 A1), so there the bell
 * stays hidden (notify.css) and a long-press on the title opens the same
 * menu, as a sheet (`openTitleNotifyMenu`); the channel's own long-press
 * menu in the list has it too.
 */
import type { Channel } from '@neosocial/protocol';
import { useState, type MouseEvent } from 'react';
import { useT } from '../i18n';
import { useFlag } from '../store/config';
import { useData } from '../store/data';
import { IconBtn } from '../ui/kit';
import { openMenu } from '../ui/overlay';
import { channelNotifyItems } from './menus';
import { isMuted } from './rules';

/** The header title's context menu (a long-press on a phone): the bell's menu. */
export function openTitleNotifyMenu(e: MouseEvent<HTMLElement>, channel: Channel): void {
  const items = channelNotifyItems(channel);
  if (!items.length) return;
  e.preventDefault();
  openMenu(e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : e.currentTarget, items);
}

export function NotifyBell({ channel }: { channel: Channel }) {
  const { t } = useT();
  const on = useFlag('notifications');
  const muted = useData((d) => isMuted(d, d.channels[channel.id] ?? channel));
  const [open, setOpen] = useState(false);
  if (!on || !channel.guild_id) return null;
  return (
    <IconBtn
      icon={muted ? 'bell-off' : 'bell'}
      className="ch-notify"
      label={t(muted ? 'nt.bellMuted' : 'nt.bell')}
      tipPos="bottom"
      aria-haspopup="menu"
      aria-expanded={open}
      pressed={muted || undefined}
      onClick={(e) => {
        const items = channelNotifyItems(channel);
        if (!items.length) return;
        setOpen(true);
        openMenu(e.currentTarget, items, {
          placement: 'bottom-end',
          onClose: () => setOpen(false),
        });
      }}
    />
  );
}
